import I18n from 'I18n';
import { ADS_LIMIT_INCREASE, API_LIMIT_INCREASE, BRAND_DOMAIN, CALCULATED_PROPERTIES_LIMIT_INCREASE, CALLING_LIMIT_INCREASE, CRM_CONTACT_RECORDS_LIMIT_INCREASE, CRM_OBJECT_LIMIT_INCREASE, CUSTOM_OBJECTS_LIMIT_INCREASE, CUSTOM_PROPERTIES_LIMIT_INCREASE, CUSTOM_SSL, DEDICATED_IP, DOMAINS_LIMIT_INCREASE, LISTS_LIMIT_INCREASE, MARKETING_SMS_ADDON, MARKETING_SMS_LIMIT_INCREASE, MARKETING_SMS_SHORT_CODE, PHONE_LIMIT_INCREASE, REPORTING_LIMIT_INCREASE, SANDBOX_LIMIT_INCREASE, TEAMS_LIMIT_INCREASE, TRANSACTIONAL_EMAIL, TRANSCRIPTION_LIMIT_INCREASE, USERS_LIMIT_INCREASE, VIDEO_LIMIT_INCREASE, WHATSAPP_LIMIT_INCREASE, WORKFLOWS_LIMIT_INCREASE } from 'self-service-api/constants/ApiNames';
import { ENTERPRISE, PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { getIsUngatedForSeats } from '../utils/gatingUtils';
import { getProductLevelInConfigs } from '../utils/productConfigUtils';
import { getSkuIdIsOrHasHub } from '../utils/productsUtils';
import { JA } from './Languages';
// helper function that checks all Product Lines and returns false for isRestricted
// if any purchased or selected option matches the passed in allowedProductLevels
const isProductLevelInAnyProductLines = (state, {
  productConfigurations,
  allowedProductLevels
}) => {
  const marketingProductLevelSelected = getProductLevelInConfigs(state, {
    productConfigurations,
    productLine: MARKETING
  });
  const serviceProductLevelSelected = getProductLevelInConfigs(state, {
    productConfigurations,
    productLine: SERVICE
  });
  const cmsProductLevelSelected = getProductLevelInConfigs(state, {
    productConfigurations,
    productLine: CMS
  });
  const salesProductLevelSelected = getProductLevelInConfigs(state, {
    productConfigurations,
    productLine: SALES
  });
  const operationsProductLevelSelected = getProductLevelInConfigs(state, {
    productConfigurations,
    productLine: OPERATIONS
  });
  const productLevelsSelected = [marketingProductLevelSelected, salesProductLevelSelected, serviceProductLevelSelected, cmsProductLevelSelected, operationsProductLevelSelected];
  return !productLevelsSelected.some(productLevel => productLevel && allowedProductLevels.includes(productLevel));
};

/**
 * Helper function that allows us to be specific about restricting the addon to
 * a specific product line(s) and specific product level(s)
 *
 * E.g. To restrict Add-on to only Sales Ent and Service Pro + Ent
 *
 * ```
 * isRestrictedProductLinesAndLevels(state, {
 *   productConfigurations,
 *   allowedProductLinesAndLevels: {
 *     [SALES]: [ENTERPRISE],
 *     [SERVICE]: [PROFESSIONAL, ENTERPRISE],
 *   }
 * })
 * ```
 *
 * @param {SubState<'productData'>} state
 * @param {{
 *  productConfigurations: ProductConfiguration[],
 *  allowedProductLinesAndLevels: Record<ProductLine, ProductLevel[]>
 * }} argObj
 * @returns {boolean}
 */
const isRestrictedProductLinesAndLevels = (state, {
  productConfigurations,
  allowedProductLinesAndLevels
}) => {
  for (const [productLine, productLevels] of Object.entries(allowedProductLinesAndLevels)) {
    const productLevelSelected = getProductLevelInConfigs(state, {
      productConfigurations,
      productLine: productLine
    });
    for (const productLevel of productLevels) {
      if (productLevelSelected === productLevel) return false;
    }
  }
  return true;
};

// Versioned entries for addon restrictions.
// fromVersion and toVersion are inclusive
// toVersion is optional (for indicating no upper limit)
const AddOnsRestrictionMap = {
  [API_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const containsAHub = productConfigurations.some(({
        skuId
      }) => getSkuIdIsOrHasHub(state, {
        skuId
      }));

      // If the user has a Hub selected, they can have the API limit increase
      return !containsAHub;
    }
  }],
  [BRAND_DOMAIN]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: MARKETING
      });
      if (marketingProductLevelSelected === ENTERPRISE) return false;
      return true;
    }
  }],
  [DOMAINS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [CMS]: [ENTERPRISE],
        [MARKETING]: [ENTERPRISE],
        [SERVICE]: [ENTERPRISE]
      }
    })
  }],
  [DEDICATED_IP]: [{
    fromVersion: 1,
    toVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: MARKETING
      });
      if (marketingProductLevelSelected === PROFESSIONAL || marketingProductLevelSelected === ENTERPRISE) {
        return false;
      }
      return true;
    }
  }, {
    fromVersion: 2,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: MARKETING
      });
      if (marketingProductLevelSelected === PROFESSIONAL || marketingProductLevelSelected === ENTERPRISE) {
        return false;
      }
      return true;
    }
  }],
  [TRANSACTIONAL_EMAIL]: [{
    fromVersion: 1,
    toVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: MARKETING
      });
      const validMarketingPlan = marketingProductLevelSelected === PROFESSIONAL || marketingProductLevelSelected === ENTERPRISE;
      if (validMarketingPlan) return false;
      return true;
    }
  }, {
    fromVersion: 2,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: MARKETING
      });
      const validMarketingPlan = marketingProductLevelSelected === PROFESSIONAL || marketingProductLevelSelected === ENTERPRISE;
      if (validMarketingPlan) return false;
      return true;
    }
  }],
  [CUSTOM_SSL]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: MARKETING
      });
      const serviceProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: SERVICE
      });
      const cmsProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: CMS
      });
      const allowedProductLevels = [PROFESSIONAL, ENTERPRISE];
      return ![serviceProductLevelSelected, marketingProductLevelSelected, cmsProductLevelSelected].some(productLevel => productLevel && allowedProductLevels.includes(productLevel));
    }
  }],
  [ADS_LIMIT_INCREASE]: [{
    fromVersion: 2,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const marketingProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: MARKETING
      });
      if (marketingProductLevelSelected === PROFESSIONAL || marketingProductLevelSelected === ENTERPRISE) {
        return false;
      }
      return true;
    }
  }],
  [REPORTING_LIMIT_INCREASE]: [{
    fromVersion: 4,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [PROFESSIONAL, ENTERPRISE]
    })
  }],
  [TEAMS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [PROFESSIONAL, ENTERPRISE]
    })
  }],
  [TRANSCRIPTION_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => {
      const salesProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: SALES
      });
      const serviceProductLevelSelected = getProductLevelInConfigs(state, {
        productConfigurations,
        productLine: SERVICE
      });
      if (salesProductLevelSelected === ENTERPRISE || serviceProductLevelSelected === ENTERPRISE) {
        return false;
      }
      return true;
    }
  }],
  [LISTS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [PROFESSIONAL, ENTERPRISE]
    })
  }],
  [SANDBOX_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [ENTERPRISE]
    })
  }],
  [CALLING_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [SALES]: [PROFESSIONAL, ENTERPRISE],
        [SERVICE]: [PROFESSIONAL, ENTERPRISE]
      }
    }) || I18n.lang === JA
  }],
  [PHONE_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [SALES]: [PROFESSIONAL, ENTERPRISE],
        [SERVICE]: [PROFESSIONAL, ENTERPRISE]
      }
    }) || I18n === JA
  }],
  [WHATSAPP_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [PROFESSIONAL, ENTERPRISE],
        [SERVICE]: [PROFESSIONAL, ENTERPRISE]
      }
    })
  }],
  [VIDEO_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [PROFESSIONAL, ENTERPRISE],
        [SERVICE]: [PROFESSIONAL, ENTERPRISE],
        [CMS]: [PROFESSIONAL, ENTERPRISE]
      }
    })
  }],
  [MARKETING_SMS_ADDON]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [PROFESSIONAL, ENTERPRISE]
      }
    })
  }],
  [MARKETING_SMS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [PROFESSIONAL, ENTERPRISE],
        [SERVICE]: [PROFESSIONAL, ENTERPRISE]
      }
    })
  }],
  [WORKFLOWS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [PROFESSIONAL, ENTERPRISE]
    })
  }],
  [USERS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isProductLevelInAnyProductLines(state, {
      productConfigurations,
      allowedProductLevels: [PROFESSIONAL, ENTERPRISE]
    }) || getIsUngatedForSeats(state)
  }],
  [CUSTOM_OBJECTS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [ENTERPRISE],
        [SALES]: [ENTERPRISE],
        [SERVICE]: [ENTERPRISE],
        [CMS]: [ENTERPRISE]
      }
    })
  }],
  [CALCULATED_PROPERTIES_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [ENTERPRISE],
        [SALES]: [ENTERPRISE],
        [SERVICE]: [ENTERPRISE],
        [CMS]: [ENTERPRISE],
        [OPERATIONS]: [ENTERPRISE]
      }
    })
  }],
  [CUSTOM_PROPERTIES_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [PROFESSIONAL, ENTERPRISE],
        [SALES]: [PROFESSIONAL, ENTERPRISE],
        [SERVICE]: [PROFESSIONAL, ENTERPRISE],
        [CMS]: [PROFESSIONAL, ENTERPRISE],
        [OPERATIONS]: [PROFESSIONAL, ENTERPRISE]
      }
    })
  }],
  [CRM_OBJECT_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [ENTERPRISE],
        [SALES]: [ENTERPRISE],
        [SERVICE]: [ENTERPRISE],
        [CMS]: [ENTERPRISE],
        [OPERATIONS]: [ENTERPRISE]
      }
    })
  }],
  [CRM_CONTACT_RECORDS_LIMIT_INCREASE]: [{
    fromVersion: 1,
    isRestricted: (state, {
      productConfigurations
    }) => isRestrictedProductLinesAndLevels(state, {
      productConfigurations,
      allowedProductLinesAndLevels: {
        [MARKETING]: [ENTERPRISE],
        [SALES]: [ENTERPRISE],
        [SERVICE]: [ENTERPRISE],
        [CMS]: [ENTERPRISE],
        [OPERATIONS]: [ENTERPRISE]
      }
    })
  }],
  [MARKETING_SMS_SHORT_CODE]: [{
    fromVersion: 1,
    /**
     * @temporary temporarily pause Marketing SMS Short Code Sales
     * @expiration 2025-06-01
     * @deprecated
     */
    isRestricted: () => true
    // (
    //   state: SubState<'productData'>,
    //   {
    //     productConfigurations,
    //   }: { productConfigurations: ProductConfiguration[] }
    // ) => {
    //   const marketingProductLevelSelected = getProductLevelInConfigs(state, {
    //     productConfigurations,
    //     productLine: MARKETING,
    //   });

    //   if (marketingProductLevelSelected === ENTERPRISE) return false;

    //   return true;
    // },
  }]
};
export const getAddonRestrictionDetails = ({
  apiName,
  versionNumber
}) => {
  if (!(apiName in AddOnsRestrictionMap)) {
    return null;
  }
  const restrictionVersions = AddOnsRestrictionMap[apiName];
  return restrictionVersions.find(({
    fromVersion,
    toVersion
  }) => versionNumber >= fromVersion && (!toVersion || versionNumber <= toVersion));
};